import React, { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { calcPosFromAngles, OrbitControls, Stats } from "@react-three/drei";
import { Leva, useControls } from "leva";

import * as THREE from "three";

import SkyBox from "./SkyBox";
import Ocean from "./Ocean";
import Boat from "./Boat";
import { BuoyImage, Buoys } from "./Buoy";
import Cloud from "./Cloud";
import Rain from "./Rain";
import CoastLine from "./CoastLine";
import state from "./config/WeatherStates.json";
import Camera from "./Camera";
import { fogFrag, fogParsFrag, fogParsVertex, fogVertex } from "./data/shaders";
import weatherPresets from "./config/WeatherStates.json";
import SceneControls from "./SceneControls";

export default function Scene({
  currentBuoy,
  beacons,
  boat,
  setBoat,
  weather,
  setWeather,
  data,
  isActive,
}) {
  const options = [
    "dawn",
    "sunrise",
    "morning",
    "noon",
    "afternoon",
    "dusk",
    "night",
  ];

  // scene state variables
  var skyState = state["skyState"][weather["skyState"]];
  const sun = weather["sunPos"];
  const sunPos = calcPosFromAngles(sun.inclination, sun.azimuth);
  sunPos.normalize();
  sunPos.multiplyScalar(15);

  // point light (used to add realism)
  const Light = () => {
    const ref = useRef();

    // var i = 1;
    // var sunPos = new THREE.Vector3();
    // var sunPosition = new THREE.Vector3();

    // useFrame((state) => {
    //   const time = state.clock.getElapsedTime();
    //   i = Math.floor((0.3 * time) % 7);
    //   skyState = weatherPresets["skyState"][options[i]];
    //   sunPosition = calcPosFromAngles(skyState.inclination, skyState.azimuth);
    //   sunPosition.normalize();
    //   sunPosition.multiplyScalar(10);
    //   sunPos.lerpVectors(ref.current.position, sunPosition, 0.01);

    //   // console.log(ref.current.uniforms.sunPosition);
    //   ref.current.position.set(sunPos.x, sunPos.y, sunPos.z);
    //   ref.current.intensity = (skyState.inclination - 0.5) * 10;
    // });

    return (
      <pointLight
        ref={ref}
        color={"white"}
        intensity={(sun.inclination - 0.5) * 10} // the higher the sun, the brighter the light
        position={sunPos}
      />
    );
  };

  // directional light (used to add shadows + sunset effect)
  const DirLight = () => {
    const ref = useRef();

    // var i = 1;
    // var sunPos = new THREE.Vector3();
    // var sunPosition = new THREE.Vector3();

    // useFrame((state) => {
    //   const time = state.clock.getElapsedTime();
    //   i = Math.floor((0.3 * time) % 7);
    //   skyState = weatherPresets["skyState"][options[i]];
    //   sunPosition = calcPosFromAngles(skyState.inclination, skyState.azimuth);
    //   sunPosition.normalize();
    //   sunPosition.multiplyScalar(10);
    //   sunPos.lerpVectors(ref.current.position, sunPosition, 0.01);

    //   // console.log(ref.current.uniforms.sunPosition);
    //   ref.current.position.set(sunPos.x, sunPos.y, sunPos.z);
    //   ref.current.intensity = 1 - (skyState.inclination - 0.5) * 8;
    // });

    return (
      <directionalLight
        ref={ref}
        color={"orange"}
        intensity={1 - (sun.inclination - 0.5) * 8} // the lower the sun, the brighter the light
        position={sunPos}
        castShadow
      />
    );
  };

  // ambient light
  const AmbiLight = () => {
    const ref = useRef();

    // var i = 1;

    // useFrame((state) => {
    //   const time = state.clock.getElapsedTime();
    //   i = Math.floor((0.3 * time) % 7);
    //   skyState = weatherPresets["skyState"][options[i]];
    //   ref.current.intensity = skyState.inclination - 0.2;
    // });

    return (
      <ambientLight
        ref={ref}
        intensity={skyState.inclination - 0.2}
        color={"white"}
      />
    );
  };

  // updated fog shader chunks - underwater
  THREE.ShaderChunk.fog_pars_vertex = fogParsVertex;
  THREE.ShaderChunk.fog_vertex = fogVertex;
  THREE.ShaderChunk.fog_pars_fragment = fogParsFrag;
  THREE.ShaderChunk.fog_fragment = fogFrag;

  return (
    <>
      <Leva
        id={"leva"}
        titleBar={{
          title: "Control Panel",
          position: { y: 60 },
        }}
        collapsed
        // hidden
      />
      <Suspense
        fallback={
          <p style={{ color: "white", textAlign: "center" }}>
            Loading Scene...
          </p>
        }
      >
        <Canvas
          shadows
          id={"canvas"}
          frameloop={isActive}
          gl={{ localClippingEnabled: true }}
        >
          {/*Camera info*/}
          <Camera>
            {/* Sky */}
            <SkyBox sunPosition={sunPos} skyState={skyState} />
          </Camera>
          {/*Scene Elements*/}
          <Ocean weather={weather} sunPosition={sunPos} boat={boat} />
          <Boat boat={boat} setBoat={setBoat} weather={weather} />
          <Cloud weather={weather} />
          <Rain weather={weather} />
          <CoastLine weather={weather} boat={boat} />
          <Buoys
            currentBuoy={currentBuoy}
            beacons={beacons}
            boat={boat}
            weather={weather}
          />
          {data && <BuoyImage path={data} />}
          {/*Lights*/}
          <AmbiLight />
          <Light />
          <DirLight />
          {/*Contols and helpers*/}
          <OrbitControls
            target={[0, 3, 0]}
            enablePan={false}
            maxDistance={70}
            minPolarAngle={Math.PI / 3}
            maxPolarAngle={Math.PI * 0.55}
            autoRotate={false}
            autoRotateSpeed={1.0}
          />
          <color attach="background" args={["#8594bd"]} />{" "}
          {/* <color attach="background" args={["white"]} />{" "} */}
          <fogExp2 attach="fog" color={"#8594bd"} density={0.03} />
          <SceneControls weather={weather} setWeather={setWeather} />
        </Canvas>
      </Suspense>
    </>
  );
}
